import { useToast } from '@cian/ui-kit/toast';
import * as React from 'react';

import { useFiltersContext } from '../../../packages/Filters/shared/utils/useFiltersContext';
import { getRangeValue } from '../../../packages/JsonQuery';
import { useApplicationContext } from '../../common/containers/ApplicationContext';
import { usePriceCorrection } from '../../hooks/usePriceCorrection';

interface ICorrectPricesParams {
  isMinChanged: boolean;
  onToastShow?(): void;
  shouldBlockApplyOnFirstToast?: boolean;
}

interface ICorrectPricesResult {
  shouldBlockApply: boolean;
}

/**
 * Хук для коррекции цен на мобильном с показом toast-уведомления.
 * Обёртка над usePriceCorrection, добавляющая:
 * - Получение данных из контекста
 * - Однократный показ toast-уведомления
 * - Применение изменений через onChange
 */
export function useMobilePriceCorrection() {
  const { jsonQuery: filtersJsonQuery, onChange } = useFiltersContext();
  const { config } = useApplicationContext();
  const toast = useToast({ indent: '72px' });
  const priceFromFilters = getRangeValue('price')(filtersJsonQuery);
  const { gte: priceMinFromFilters = null, lte: priceMaxFromFilters = null } = priceFromFilters || {};

  // Получаем минимальный разбег между ценами из конфига (по умолчанию 1000₽)
  const pricesMinGap = React.useMemo(() => {
    return config.get<number>('Dailyrent.PriceInputs.MinGap') || 1000;
  }, [config]);

  // Флаг для показа уведомления только один раз
  const hasShownToastRef = React.useRef(false);

  // Хук для расчёта скорректированных цен
  const calculateCorrectedPrice = usePriceCorrection({ pricesMinGap });

  // Функция для коррекции цен
  return React.useCallback(
    ({
      isMinChanged,
      onToastShow,
      shouldBlockApplyOnFirstToast = false,
    }: ICorrectPricesParams): ICorrectPricesResult => {
      const { adjustedMin, adjustedMax, wasCorrected } = calculateCorrectedPrice({
        newMin: priceMinFromFilters,
        newMax: priceMaxFromFilters,
        isMinChanged,
      });

      if (wasCorrected) {
        onChange({ action: 'setPrice', arguments: [adjustedMin, adjustedMax] });

        // Показываем уведомление при первой коррекции
        if (!hasShownToastRef.current) {
          hasShownToastRef.current = true;
          toast.open({
            type: 'info',
            text: 'Округляем цену, чтобы вы видели больше вариантов',
          });
          onToastShow?.();

          // Если нужно заблокировать применение (только для handleApply при первом показе toast)
          // Блокировка нужна для случая когда пользователь работает с инпутами без софтовой клавиатуры
          // например используя физическую клавиатуру или рукописный ввод,
          // тогда кнопки применения нужно выключать до коррекции цены, иначе пользак применит не скорректированные цены,
          // а в случае софтовой клавиатуры мы прячем кнопки, там блокировка по сути не нужна.
          if (shouldBlockApplyOnFirstToast) {
            return { shouldBlockApply: true };
          }
        }
      }

      return { shouldBlockApply: false };
    },
    [priceMinFromFilters, priceMaxFromFilters, calculateCorrectedPrice, onChange, toast],
  );
}
